/**
 * Per-service depth: tech stack, FAQs, engagement model and visual showcase.
 *
 * This is what turns twelve identical service templates into twelve distinct pages.
 * FAQs are rendered with FAQPage schema.org markup, so they are also the site's
 * highest-value long-tail SEO surface — keep answers specific and honest.
 *
 * Showcase images: set `src` to a path under `frontend/public/assets/...` when real
 * artwork is ready. While `src` is null, a branded placeholder renders in its place.
 */
import { EDUANANT } from "./eduanant.js";

export const SERVICE_DETAILS = {
  "product-development": {
    techStack: ["React", "Node.js", "Python", "PostgreSQL", "MongoDB", "Docker", "AWS"],
    engagement: {
      model: "Fixed-scope MVP, then monthly product retainer",
      timeline: "8–16 weeks to first production release",
      team: "1 product lead, 2 engineers, 1 designer",
      cadence: "Weekly demo, fortnightly release",
    },
    showcase: [
      { src: null, alt: "EduAnant product dashboard built by Snapx Technologies", caption: "EduAnant — our own SaaS, in production", ratio: "16/9" },
      { src: null, alt: "Product architecture diagram for a multi-tenant SaaS platform", caption: "Architecture before code", ratio: "16/9" },
      { src: null, alt: "MVP scoping workshop board mapping features to release waves", caption: "Scoping — what ships first, and why", ratio: "16/9" },
    ],
    faq: [
      {
        q: "How long does it take to build an MVP?",
        a: "Eight to sixteen weeks for a production MVP, depending on scope. We spend the first two weeks scoping ruthlessly — deciding what does not go into version one is usually more valuable than deciding what does. You get a working release you can put in front of real users at the end, not a prototype.",
      },
      {
        q: "Do you work with non-technical founders?",
        a: "Most of our product clients are non-technical. We handle architecture, hosting, security and app store submissions, and we translate every technical decision into the business trade-off behind it. You never have to take a decision on faith.",
      },
      {
        q: "Who owns the code and the infrastructure?",
        a: "You do, completely. Code lives in your repository from day one, infrastructure runs in your cloud account, and we hand over documentation and credentials at the end of every engagement. There is no lock-in and no licence fee to us.",
      },
      {
        q: "What happens after launch?",
        a: "Launch is the start of the work, not the end. Most clients move onto a monthly retainer covering iteration, monitoring, bug fixes and new features. If you would rather hire in-house, we help you recruit and hand over cleanly.",
      },
      {
        q: "Have you actually shipped a product of your own?",
        a: `Yes — EduAnant, a school operating system with ${EDUANANT.modules} modules, teacher and parent portals and an Android app on Google Play. It has run ${EDUANANT.liveSchool.name} every working day since ${EDUANANT.liveSchool.since}. Everything we recommend to you, we have already had to live with ourselves.`,
      },
    ],
  },

  "web-development": {
    techStack: ["React", "Next.js", "Node.js", "Tailwind CSS", "TypeScript", "Vercel", "Cloudflare"],
    engagement: {
      model: "Fixed-scope build, optional care plan",
      timeline: "4–10 weeks from kickoff to launch",
      team: "1 designer, 1–2 engineers",
      cadence: "Weekly staging build you can click through",
    },
    showcase: [
      { src: null, alt: "S.D.V. Public School website homepage built by Snapx Technologies", caption: "S.D.V. Public School — sdv-edu.in", ratio: "16/9" },
      { src: null, alt: "Lighthouse performance report showing green scores for a Snapx build", caption: "Green Lighthouse, every build", ratio: "16/9" },
      { src: null, alt: "Responsive website layouts across desktop, tablet and mobile", caption: "Designed mobile-first", ratio: "16/9" },
    ],
    faq: [
      {
        q: "How much does a website cost?",
        a: "It depends on page count, whether you need a CMS, and how much content and design work is involved. A focused marketing site is a very different engagement from a logged-in web application. Tell us what you need on the contact form and we will send a fixed scope and price — not an hourly estimate that drifts.",
      },
      {
        q: "How fast will my site be?",
        a: "We target sub-two-second loads on a mid-range Android phone on 4G, and green Core Web Vitals. That means real image optimisation, code splitting and no bloated page builders. We show you the Lighthouse report before we call it done.",
      },
      {
        q: "Can I update the content myself?",
        a: "Yes. We wire up a headless CMS (Sanity, Strapi or similar) for any content you will realistically change — announcements, blog posts, team, pricing. We deliberately do not put structural layout behind a CMS, because that is how fast sites become slow ones.",
      },
      {
        q: "Do you redesign existing websites?",
        a: "Often. We audit what is there, keep the URLs and content that already rank, and set up redirects for anything that moves — so a redesign does not cost you the search traffic you have spent years earning.",
      },
      {
        q: "What do you need from me to start?",
        a: "Your logo files, brand colours if they exist, the content you already have, and access to your domain. If any of that is missing we can produce it — content structure and copywriting are part of what we do.",
      },
    ],
  },

  "mobile-apps": {
    techStack: ["React Native", "Flutter", "Kotlin", "Swift", "Firebase", "Node.js"],
    engagement: {
      model: "Fixed-scope build plus store launch",
      timeline: "10–20 weeks including store review",
      team: "1 designer, 2 mobile engineers, 1 QA",
      cadence: "Fortnightly TestFlight / internal-track build",
    },
    showcase: [
      { src: null, alt: "EduAnant school app running on an Android phone", caption: "EduAnant — live on Google Play", ratio: "3/4" },
      { src: null, alt: "Mobile app design system showing components and states", caption: "One design system, both platforms", ratio: "16/9" },
      { src: null, alt: "Offline-first sync architecture diagram for a mobile application", caption: "Offline-first by default", ratio: "16/9" },
    ],
    faq: [
      {
        q: "Native or cross-platform — which should I choose?",
        a: "Cross-platform (React Native or Flutter) for most business apps: one codebase, two stores, significantly lower cost. Native when you depend on heavy device hardware — serious camera work, background location, AR. We will tell you honestly which side your app falls on.",
      },
      {
        q: "Will the app work on cheap phones and patchy networks?",
        a: "That is the design constraint we start from. We build offline-first, keep payloads small, test on mid-range Android devices rather than flagship phones, and assume the network will drop mid-action — because in India it does.",
      },
      {
        q: "Do you handle App Store and Play Store submission?",
        a: "Yes, end to end — developer accounts, store listings, screenshots, privacy declarations, review responses and phased rollout. We have shipped and maintained our own app on Google Play, so the review process holds no surprises.",
      },
      {
        q: "What about updates after launch?",
        a: "Apps need ongoing work: OS updates, store policy changes, crash fixes. Most clients keep us on a light monthly retainer for exactly this. We set up crash reporting and analytics from day one so problems surface before your users report them.",
      },
      {
        q: "Can the app work with my existing website or system?",
        a: "Yes. We integrate with whatever you already run — an existing backend, an ERP, a payment gateway, a school or hospital system. If there is no API, we build one.",
      },
    ],
  },

  "e-commerce": {
    techStack: ["Shopify", "WooCommerce", "Next.js", "Razorpay", "Shiprocket", "Meta Pixel"],
    engagement: {
      model: "Fixed-scope storefront, then growth retainer",
      timeline: "5–12 weeks to first order",
      team: "1 designer, 1–2 engineers, 1 growth lead",
      cadence: "Weekly build review, post-launch conversion sprints",
    },
    showcase: [
      { src: null, alt: "D2C storefront product listing page designed by Snapx Technologies", caption: "Storefront built for conversion", ratio: "16/9" },
      { src: null, alt: "Mobile checkout flow optimised for Indian payment methods", caption: "Checkout — UPI, cards, COD", ratio: "3/4" },
      { src: null, alt: "E-commerce analytics dashboard showing funnel drop-off by step", caption: "Every drop-off, measured", ratio: "16/9" },
    ],
    faq: [
      {
        q: "Shopify or a custom build?",
        a: "Shopify or WooCommerce if you are selling a normal catalogue and want to be live quickly — the platform handles payments, tax and inventory so you do not pay us to rebuild them. Custom when your model is genuinely unusual: marketplaces, subscriptions with complex rules, B2B pricing tiers.",
      },
      {
        q: "Which payment and delivery options do you integrate?",
        a: "Razorpay, PayU, Cashfree and Stripe for payments — including UPI, cards, netbanking, wallets and EMI. Shiprocket, Delhivery and Shadowfax for logistics, with COD flows and RTO handling, because cash on delivery is still a large share of Indian orders.",
      },
      {
        q: "Can you improve the conversion rate on my existing store?",
        a: "Yes, and it is usually the cheapest growth available to you. We audit the funnel step by step, find where people abandon, and fix it — page speed, checkout friction, trust signals, product page structure. We measure before and after so you can see what the work bought.",
      },
      {
        q: "Do you handle product photography and listings?",
        a: "We handle listing structure, copy, categorisation and SEO for product pages. For photography we work with a shoot partner or with images you supply — and we will tell you plainly when your current images are costing you sales.",
      },
      {
        q: "Will my store be fast on mobile?",
        a: "It has to be — the majority of Indian e-commerce traffic is mid-range Android on 4G, and every second of load time costs conversions. We optimise images, defer non-critical scripts and test on real devices, not just on a laptop.",
      },
    ],
  },

  "ui-ux-design": {
    techStack: ["Figma", "FigJam", "Framer", "Maze", "Hotjar", "Storybook"],
    engagement: {
      model: "Discovery sprint, then design system + product UI",
      timeline: "3–8 weeks depending on product surface",
      team: "1 researcher, 1–2 product designers",
      cadence: "Twice-weekly review in Figma",
    },
    showcase: [
      { src: null, alt: "Design system component library with buttons, forms and states", caption: "Design systems engineers can build from", ratio: "16/9" },
      { src: null, alt: "User journey map produced during a discovery sprint", caption: "Journey maps from real interviews", ratio: "16/9" },
      { src: null, alt: "High fidelity product interface designs for a SaaS dashboard", caption: "Interfaces designed around evidence", ratio: "16/9" },
    ],
    faq: [
      {
        q: "What is the difference between UI and UX in your process?",
        a: "UX is deciding what the screens should be and in what order — research, journey maps, information architecture, wireframes. UI is how those screens look and feel — type, colour, spacing, motion, states. We do both, and we do them in that order, because a beautiful interface to the wrong flow is expensive decoration.",
      },
      {
        q: "Do you do user research, or just design?",
        a: "We research. Interviews with your real users, a review of your analytics and support tickets, and usability testing on the prototype before anything gets built. It is the cheapest possible moment to discover you were wrong.",
      },
      {
        q: "Will my developers be able to build what you design?",
        a: "Yes — we hand over a Figma file with a real component library, defined tokens for colour, type and spacing, every interactive state, and responsive behaviour specified. We are engineers too, so we do not design things that cannot be built.",
      },
      {
        q: "Can you redesign an existing product without breaking it?",
        a: "Yes. We usually run a UX audit first, prioritise fixes by impact against effort, and roll changes out in phases so your existing users are never dropped into an unfamiliar product overnight.",
      },
      {
        q: "Do you provide a design system?",
        a: "For any product beyond a handful of screens, yes — and we strongly recommend it. A design system is what stops your product drifting into twelve slightly different button styles by month six.",
      },
    ],
  },

  "data-analytics": {
    techStack: ["Python", "SQL", "dbt", "BigQuery", "Metabase", "Power BI", "Airflow"],
    engagement: {
      model: "Data audit, pipeline build, then reporting retainer",
      timeline: "4–10 weeks to first trusted dashboard",
      team: "1 data engineer, 1 analyst",
      cadence: "Weekly dashboard review with your team",
    },
    showcase: [
      { src: null, alt: "Business intelligence dashboard showing revenue and retention metrics", caption: "One dashboard, every question answered", ratio: "16/9" },
      { src: null, alt: "Data pipeline architecture moving data from sources into a warehouse", caption: "Pipelines that run themselves", ratio: "16/9" },
      { src: null, alt: "Marketing attribution report tracing revenue back to campaigns", caption: "Attribution you can defend", ratio: "16/9" },
    ],
    faq: [
      {
        q: "My data is spread across spreadsheets and three different tools. Can you still help?",
        a: "That is the normal starting point, and it is exactly the problem we solve. We map every source you have, build pipelines that pull them into one warehouse, and reconcile the definitions — so 'active customer' means one thing across the whole business.",
      },
      {
        q: "How long before I see a dashboard?",
        a: "Usually four to ten weeks to a dashboard you can actually trust. We can produce something that draws charts much faster than that; the time goes into making the numbers correct, which is the only thing that makes a dashboard worth having.",
      },
      {
        q: "Do I need a data warehouse?",
        a: "If you have more than two or three data sources, yes — it is cheaper than the alternative, which is analysts manually reconciling exports every month. For smaller setups we can often report directly off your production database without adding cost.",
      },
      {
        q: "Which dashboard tool do you use?",
        a: "Metabase or Power BI for most clients — Metabase when you want your team writing their own questions, Power BI when you are already in the Microsoft ecosystem. We are not tied to a vendor and we will not sell you licences you do not need.",
      },
      {
        q: "Can you set up tracking on my website and ads?",
        a: "Yes — analytics, server-side event tracking, conversion tracking and attribution across Google, Meta and organic. Done properly, so you stop guessing which channel actually produced the revenue.",
      },
    ],
  },

  "cloud-solutions": {
    techStack: ["AWS", "Google Cloud", "Docker", "Kubernetes", "Terraform", "GitHub Actions", "Grafana"],
    engagement: {
      model: "Architecture review, migration project, then DevOps retainer",
      timeline: "3–12 weeks depending on what is being moved",
      team: "1 cloud architect, 1 DevOps engineer",
      cadence: "Weekly checkpoint, staged cutover plan",
    },
    showcase: [
      { src: null, alt: "Cloud architecture diagram with load balancing and auto-scaling", caption: "Architecture that absorbs growth", ratio: "16/9" },
      { src: null, alt: "CI/CD pipeline running automated build test and deploy stages", caption: "Every deploy, automated and reversible", ratio: "16/9" },
      { src: null, alt: "Infrastructure monitoring dashboard with uptime and cost metrics", caption: "Uptime and spend, both watched", ratio: "16/9" },
    ],
    faq: [
      {
        q: "Which cloud should I be on?",
        a: "AWS or Google Cloud for most workloads. The honest answer is that the platform matters far less than how it is configured — we have seen far more money lost to unmonitored instances than to picking the 'wrong' provider.",
      },
      {
        q: "Can you migrate us without downtime?",
        a: "In almost every case, yes. We run the new environment in parallel, replicate data, test the cutover on a rehearsal run, and switch DNS during your quietest window with a rollback ready. Anything that genuinely needs a maintenance window, we tell you about up front.",
      },
      {
        q: "Our cloud bill keeps growing. Can you reduce it?",
        a: "Usually, and often substantially. The common causes are over-provisioned instances, forgotten environments, unattached storage and no reserved-capacity planning. We audit spend, fix the obvious waste first, then set up alerting so it does not creep back.",
      },
      {
        q: "Do you set up backups and disaster recovery?",
        a: "Yes, and we test the restore — an untested backup is a belief, not a backup. We agree a recovery point and recovery time objective with you, then build to it and rehearse it.",
      },
      {
        q: "Can you work alongside our in-house developers?",
        a: "Frequently. We set up the infrastructure, CI/CD and monitoring, document it, and train your team to run it. The goal is that you do not need us permanently.",
      },
    ],
  },

  "cyber-security": {
    techStack: ["OWASP ZAP", "Burp Suite", "Nmap", "Snyk", "Cloudflare", "Vault"],
    engagement: {
      model: "Point-in-time audit, remediation sprint, optional monitoring",
      timeline: "2–6 weeks for audit and remediation",
      team: "1 security engineer, 1 developer for fixes",
      cadence: "Findings report, then prioritised fix sprints",
    },
    showcase: [
      { src: null, alt: "Security audit findings report ranked by severity", caption: "Findings ranked by real risk", ratio: "16/9" },
      { src: null, alt: "Application hardening checklist covering authentication and access control", caption: "Hardening, checklist by checklist", ratio: "16/9" },
      { src: null, alt: "Security monitoring dashboard showing blocked requests and alerts", caption: "Monitoring after the fix", ratio: "16/9" },
    ],
    faq: [
      {
        q: "We are small. Are we really a target?",
        a: "Most attacks are not targeted — they are automated scans looking for known vulnerabilities on any server that answers. Being small makes you less interesting to a human attacker and no less visible to a bot. The basics matter more than the budget.",
      },
      {
        q: "What does a security audit actually involve?",
        a: "We test your application the way an attacker would: authentication and session handling, access control between accounts, injection, file uploads, exposed configuration and dependencies with known vulnerabilities. You get a report ranked by real risk, with reproduction steps and fixes — not a scanner dump.",
      },
      {
        q: "Do you fix the problems or just report them?",
        a: "Both, if you want. Many clients take the report to their own developers; others have us do the remediation and re-test. We are happy either way and we re-test for free after fixes we specified.",
      },
      {
        q: "Can you help with compliance requirements?",
        a: "We help with the technical side — access controls, encryption, audit logging, data retention and the documentation of it — for requirements like India's DPDP Act, ISO 27001 preparation and enterprise customer security questionnaires. We are not a certification body and we will say so.",
      },
      {
        q: "How often should we be audited?",
        a: "Annually as a baseline, and after any significant change — a new payment flow, a new integration, a major release. Continuous dependency scanning in CI should run on every commit, and that is cheap to set up.",
      },
    ],
  },

  "digital-marketing": {
    techStack: ["Google Ads", "Meta Ads", "GA4", "Search Console", "Ahrefs", "Klaviyo", "WhatsApp API"],
    engagement: {
      model: "Monthly retainer plus media spend",
      timeline: "First campaigns live in 2–3 weeks",
      team: "1 strategist, 1 media buyer, 1 designer",
      cadence: "Weekly performance call, monthly review",
    },
    showcase: [
      { src: null, alt: "Performance marketing dashboard showing cost per acquisition by channel", caption: "Every rupee traced to a result", ratio: "16/9" },
      { src: null, alt: "Ad creative variations tested across Meta and Google campaigns", caption: "Creative, tested not guessed", ratio: "16/9" },
      { src: null, alt: "Organic search ranking growth chart over six months", caption: "SEO compounding over time", ratio: "16/9" },
    ],
    faq: [
      {
        q: "How much should I spend on ads to start?",
        a: "Enough to gather data before you draw conclusions — for most Indian B2C campaigns that means a meaningful test budget over four to six weeks, not a token amount over one. We will tell you what your category realistically costs before you commit, and we would rather turn down a budget that cannot work than take it.",
      },
      {
        q: "SEO or paid ads — which first?",
        a: "Paid for immediate demand, SEO for durable demand. If you need leads this quarter, start paid. If you want your cost per lead to fall over two years, start SEO now as well. Most clients run both, weighted by how urgent revenue is.",
      },
      {
        q: "How long until SEO shows results?",
        a: "Three to six months for meaningful movement on competitive terms, faster for local and long-tail queries. Anyone promising you page one in thirty days is either targeting terms nobody searches or doing something that will eventually be penalised.",
      },
      {
        q: "Do you handle the creative as well as the media buying?",
        a: "Yes — that is the point of having design, motion and media under one roof. Creative is the single biggest lever on ad performance, and it moves fastest when the person buying the media can brief the designer directly.",
      },
      {
        q: "What reporting do I get?",
        a: "A live dashboard you can open any day, plus a monthly review that explains what we changed and why. We report on revenue and cost per acquisition, not impressions — vanity metrics are how agencies hide.",
      },
    ],
  },

  branding: {
    techStack: ["Illustrator", "InDesign", "Figma", "After Effects", "Photoshop"],
    engagement: {
      model: "Fixed-scope identity project",
      timeline: "6–12 weeks from strategy to guidelines",
      team: "1 brand strategist, 1–2 designers",
      cadence: "Three presented rounds: strategy, direction, system",
    },
    showcase: [
      { src: null, alt: "Basilius International brand identity designed by Snapx Technologies", caption: "Basilius — ultra-luxury real estate", ratio: "16/9" },
      { src: null, alt: "L ANANTH brand mark and stationery system", caption: "L ANANTH — global trade", ratio: "4/3" },
      { src: null, alt: "Brand guidelines document showing logo usage and colour rules", caption: "Guidelines your team can follow", ratio: "3/2" },
    ],
    faq: [
      {
        q: "What do I actually get in a branding project?",
        a: "Brand strategy (positioning, audience, voice), a primary logo with its variants and clear-space rules, a typographic and colour system, application across the collateral you actually use, and a guidelines document so the brand survives contact with other designers.",
      },
      {
        q: "How is this different from just buying a logo?",
        a: "A logo is one asset; a brand is the system that makes every future asset consistent. Without the system, you get a nice mark and then twelve months of ads, decks and packaging that look like they came from different companies.",
      },
      {
        q: "How many logo options do you present?",
        a: "Two or three genuinely different directions, each argued from the strategy — not twenty variations of the same idea. We would rather defend a small number of considered options than let you pick a favourite from a wall of noise.",
      },
      {
        q: "Do you help with naming?",
        a: "Yes, including trademark screening guidance and domain availability checks. Naming is a separate phase before identity design, because the name shapes everything that follows.",
      },
      {
        q: "Can you rebrand without losing our existing recognition?",
        a: "Yes — that is an evolution rather than a revolution, and it is often the right call for an established business. We identify which brand assets your customers actually recognise, keep those, and modernise around them.",
      },
    ],
  },

  "motion-graphics": {
    techStack: ["After Effects", "Cinema 4D", "Premiere Pro", "Lottie", "Rive", "Figma"],
    engagement: {
      model: "Per-film fixed scope, or monthly social video retainer",
      timeline: "3–6 weeks per explainer film",
      team: "1 writer, 1 designer, 1 animator",
      cadence: "Script → storyboard → animatic → final",
    },
    showcase: [
      { src: null, alt: "Product explainer film storyboard frames", caption: "Storyboards before a frame is animated", ratio: "16/9" },
      { src: null, alt: "Animated brand identity reveal frames", caption: "Brand motion language", ratio: "16/9" },
      { src: null, alt: "UI micro-interaction animations built with Lottie", caption: "Interface motion, shipped as Lottie", ratio: "16/9" },
    ],
    faq: [
      {
        q: "How long should our explainer video be?",
        a: "Sixty to ninety seconds for a product explainer, fifteen to thirty for social. The constraint is useful: if you cannot explain the product in ninety seconds, the problem is usually the positioning, not the runtime.",
      },
      {
        q: "What is your process for a film?",
        a: "Script first, then storyboard, then an animatic with timing and voiceover, then final animation. You approve at every stage — which means nobody discovers a structural problem after eighty hours of animation.",
      },
      {
        q: "Do you write the script and provide voiceover?",
        a: "Yes to both. Scriptwriting is included, and we cast professional voice artists in English or Hindi. Music and sound design are licensed and included in the delivery.",
      },
      {
        q: "Can you animate our existing brand?",
        a: "Yes. If you have brand guidelines we work inside them and extend them into motion — how the logo behaves, how transitions feel, what the easing curves are. If you do not have guidelines, we can produce the motion half of them.",
      },
      {
        q: "Do you do UI animation for apps and websites?",
        a: "Yes — micro-interactions, loading states and transitions, delivered as Lottie or Rive files your developers can drop straight in, along with the specifications for how they should trigger.",
      },
    ],
  },

  "finance-gst": {
    techStack: ["Tally", "Zoho Books", "QuickBooks", "GST Portal", "MCA21"],
    engagement: {
      model: "Monthly compliance retainer",
      timeline: "Onboarding in 1–2 weeks, then ongoing",
      team: "1 accountant, CA oversight",
      cadence: "Monthly filing calendar, quarterly review",
    },
    showcase: [
      { src: null, alt: "GST filing calendar showing monthly and quarterly deadlines", caption: "A filing calendar you never think about", ratio: "16/9" },
      { src: null, alt: "Monthly bookkeeping report with profit and loss summary", caption: "Books closed every month", ratio: "16/9" },
      { src: null, alt: "Company incorporation documentation checklist", caption: "Incorporation, handled", ratio: "4/3" },
    ],
    faq: [
      {
        q: "Do I need to register for GST?",
        a: "Generally once turnover crosses the threshold for your state and business type, and immediately for inter-state supply, e-commerce sales and certain other categories regardless of turnover. Thresholds change — tell us your situation and we will confirm against the current rules rather than a number we memorised.",
      },
      {
        q: "What happens if I have missed filings?",
        a: "We can bring you back into compliance. Late fees and interest apply and cannot be waived by us, but the sooner it is filed the smaller the number gets. We assess the backlog, tell you what it will cost, and file in the right order.",
      },
      {
        q: "Can you handle bookkeeping as well as filing?",
        a: "Yes — monthly bookkeeping, reconciliation, GST returns, TDS and annual filings under one retainer, with CA oversight. Filing is much cheaper and much more accurate when the books behind it are already clean.",
      },
      {
        q: "Do you help with incorporation?",
        a: "Yes — private limited, LLP and OPC registration, PAN, TAN, GST registration and current account assistance. We incorporated ourselves as an LLP in 2021, so the paperwork is familiar territory.",
      },
      {
        q: "Why does a technology company offer accounting?",
        a: "Because our clients kept asking. A founder building a product also needs to be incorporated, GST-registered and filing on time, and stitching that together across three vendors wastes their attention. One team, one accountable relationship.",
      },
    ],
  },
};
